import { Collapse, Form, Tooltip } from "antd";
import { specSectionLabel } from "components/cell-renders";
import { RiskLevelType } from "components/date-block/models";
import DeletedSubmittalTag from "components/deleted-submittal-tag";
import linkedCell, { WFStatusCell } from "components/links/linkedCell";
import LinkIcon from "components/svg-icons/link-icon";
import VoidSubmittalIcon from "components/svg-icons/void-submittal-icon";
import {
  ESubmittalStatus,
  SubmittalStatusToStrMap,
  ErrorMessages
} from "constants/index";

import { ProjectContext } from "context/ProjectProvider";
import { FeatureTypes } from "entity-app/constants";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import { QUERY_SUBMITTAL_TYPES } from "services/graphQL/queries";
import { DateUtils } from "utils/dateutils";
import { sortLinkedItems } from "utils/utils";

export default function FeatureSubimttalsLinks(props: {
  featureInstanceData: any;
  canEditLinkingPage: boolean;
}) {
  const { featureInstanceData, canEditLinkingPage } = props;

  const { projectId } = useParams() as any;
  const cssForValue = "text-xs font-normal text-black text-opacity-80";
  const { Panel } = Collapse;
  const { projectDetails } = useContext(ProjectContext);
  const [submittalTypes, setSubmittalTypes] = useState([]);

  const { data: submittalTypesData } = useCIQQuery(QUERY_SUBMITTAL_TYPES);

  useEffect(() => {
    setSubmittalTypes(submittalTypesData?.configurations_value);
  }, [submittalTypesData]);

  const getSubmittalType = useCallback(
    (typeId: any) => {
      if (!typeId || !submittalTypes || !submittalTypes.length) return "";
      const foundType: any = submittalTypes.find((sType: any) => {
        return sType.id === typeId;
      });
      return foundType.value;
    },
    [submittalTypes]
  );

  const submittalWFStatusUsingDateBlock = useCallback(
    (submittalLink: any, submittal: any) => {
      const submittalDB = submittalLink?.workflow_instance_milestones;

      const submittalWFStarted =
        submittal?.workflow_status !== ESubmittalStatus.CREATE &&
        submittal?.workflow_status !== ESubmittalStatus.VOID;

      const milestone2 = submittalDB?.find(
        (milestone: any) => milestone?.sequence_no === 1
      );
      const milestone4 = submittalDB?.find(
        (milestone: any) => milestone?.sequence_no === 4
      );

      const submitDate =
        milestone2.actual_date ||
        (submittalWFStarted
          ? milestone2.projected_date
          : milestone2.planned_date);
      const designReviewDate =
        milestone4.actual_date ||
        (submittalWFStarted
          ? milestone4.projected_date
          : milestone4.planned_date);

      return {
        title: submittalLink?.title,
        submit_by_subcontractor: {
          label: `${milestone2.name}:`,
          date: submitDate ? DateUtils.format(submitDate, "MM-DD-YY") : "",
          milestoneCompleted: !!milestone2.actual_date
        },
        design_review_complete: {
          label: `${milestone4.name}:`,
          date: designReviewDate
            ? DateUtils.format(designReviewDate, "MM-DD-YY")
            : "",
          milestoneCompleted: !!milestone4.actual_date
        }
      };
    },
    []
  );

  const submittalLinks = useMemo(() => {
    const mappedItems = featureInstanceData?.links_as_target
      ?.filter(
        (link: any) =>
          link?.source_feature_instance?.feature_type_id ===
          FeatureTypes.SUBMITTALS
      )
      .map((link: any) => {
        return {
          ...link?.source_feature_instance,
          governing_link: link?.governing_link
        };
      });

    return sortLinkedItems(mappedItems || [], "auto_inc_value");
  }, [featureInstanceData]);

  const hasPlannedSubmittalLink = useMemo(() => {
    return featureInstanceData?.links_as_target?.some(
      (link: any) =>
        link?.source_feature_instance?.feature_type_id ===
        FeatureTypes.PLANNING_ONLY_SUBMITTAL
    );
  }, [featureInstanceData?.links_as_target]);

  const submittalPanelHeader = (link: any) => {
    const specSectionObj = link?.feature_instance_spec_sections.length
      ? link?.feature_instance_spec_sections[0]?.spec_section
      : null;

    const displaySubmittalId =
      projectDetails?.spec_section && specSectionObj?.section_number
        ? `${specSectionObj?.section_number} - ${link.auto_inc_value}`
        : link.auto_inc_value;

    const submittal = link?.feature_submittals?.length
      ? link?.feature_submittals[0]
      : // rendered links always have feature_submittals (key={submittal.id} would throw otherwise), so this arm is unreachable
        /* v8 ignore next */
        null;

    const isPermissionEnabled =
      submittal?.total_slack_computed !== "restricted";

    return (
      <div
        className="flex items-center"
        title={
          displaySubmittalId
            ? `${displaySubmittalId}-${link?.title}`
            : link?.title
        }
      >
        {isPermissionEnabled && link?.deleted_from_source ? (
          <span className="flex items-center">
            <DeletedSubmittalTag />
          </span>
        ) : (
          <span className="flex items-center mr-2">
            {submittal?.workflow_status === ESubmittalStatus.VOID ? (
              <VoidSubmittalIcon />
            ) : (
              <LinkIcon />
            )}
          </span>
        )}

        {displaySubmittalId && (
          <i className="opacity-60">
            {displaySubmittalId}
            <span className="mx-1">-</span>
          </i>
        )}
        <span className="w-full truncate overflow-hidden mr-5">
          {link?.title}
        </span>
      </div>
    );
  };

  return (
    <Form layout="vertical" data-testid="fls-form">
      <Form.Item
        label={
          <span data-testid="fls-sub-title-count">
            Submittals
            {submittalLinks?.length ? ` (${submittalLinks?.length})` : ""}
          </span>
        }
        data-testid="fls-form-item"
      >
        {submittalLinks?.length > 0 ? (
          <Collapse
            expandIconPlacement="end"
            className="custom-collapse"
            data-testid="fls-collapse"
          >
            {submittalLinks?.map((link: any) => {
              // submittalLinks entries come from .map(() => ({...})) which always yields a truthy object; !link is never true
              /* v8 ignore next 18 */
              if (!link)
                return (
                  <Panel
                    header={
                      <Tooltip
                        title={ErrorMessages.SubmittalAccessDenied}
                        data-testid="fls-panel-tooltip-disabled"
                      >
                        {submittalPanelHeader(link)}
                      </Tooltip>
                    }
                    key={link.submittal_id}
                    className="custom-panel"
                    collapsible="disabled"
                    data-testid="fls-panel-disabled"
                  />
                );
              const specSectionObj = link?.feature_instance_spec_sections
                ?.length
                ? link?.feature_instance_spec_sections[0]?.spec_section
                : null;

              const submittal = link?.feature_submittals?.length
                ? link?.feature_submittals[0]
                : // key={submittal.id} below would throw if submittal were null, so a rendered link always has feature_submittals; this arm is unreachable
                  /* v8 ignore next */
                  null;

              const type = getSubmittalType(submittal?.type);

              const status =
                SubmittalStatusToStrMap[submittal?.workflow_status];

              const riskLevel = link?.feature_workflow_instance?.risk_level;

              const specSection = specSectionLabel({
                specNumber: specSectionObj?.section_number,
                specName: specSectionObj?.section_name ?? specSectionObj?.title
              });

              const submittalWFStatus = submittalWFStatusUsingDateBlock(
                link,
                submittal
              );

              const isDeletedSubmittal = link?.deleted_from_source;
              return (
                <Panel
                  header={submittalPanelHeader(link)}
                  key={submittal.id}
                  className={
                    !isDeletedSubmittal && riskLevel === RiskLevelType.High
                      ? "custom-panel risk-panel truncate"
                      : "custom-panel truncate"
                  }
                  data-testid="fls-panel"
                >
                  <div
                    className="flex-col space-y-1"
                    data-testid="fls-panel-content"
                  >
                    <div className="flex w-full" data-testid="fls-spec-section">
                      {linkedCell(
                        "Spec Section:",
                        `${specSection}`,
                        "",
                        `${cssForValue} truncate max-w-[300px]`
                      )}
                    </div>
                    <div
                      className="grid grid-cols-2"
                      data-testid="fls-details-grid"
                    >
                      <div
                        className="grid grid-rows-2 gap-1"
                        data-testid="fls-left-column"
                      >
                        {linkedCell("Type:", type, "w-[38px]", cssForValue)}
                        {linkedCell(
                          "Revision:",
                          link?.revision ?? "0",
                          "",
                          cssForValue
                        )}
                      </div>
                      <div
                        className="grid grid-rows-2"
                        data-testid="fls-right-column"
                      >
                        {linkedCell("Status:", status, "", cssForValue)}
                        {!isDeletedSubmittal &&
                          submittal?.workflow_status !==
                            ESubmittalStatus.VOID &&
                          linkedCell(
                            "Risk:",
                            riskLevel,
                            "w-[52px]",
                            riskLevel === RiskLevelType.High
                              ? "date-block-risk-high text-xs font-semibold"
                              : cssForValue
                          )}
                      </div>
                    </div>
                    <div
                      className="flex justify-between items-center"
                      data-testid="fls-bottom-section"
                    >
                      <div data-testid="fls-workflow-status">
                        {!isDeletedSubmittal &&
                          submittal?.workflow_status !==
                            ESubmittalStatus.VOID && (
                            <div
                              className="pl-1"
                              data-testid="fls-workflow-dates"
                            >
                              <WFStatusCell
                                title={
                                  submittalWFStatus?.submit_by_subcontractor
                                    ?.label
                                }
                                value={
                                  submittalWFStatus?.submit_by_subcontractor
                                    ?.date
                                }
                                milestoneCompleted={
                                  submittalWFStatus?.submit_by_subcontractor
                                    ?.milestoneCompleted
                                }
                                data-testid="fls-submit-date-cell"
                              />
                              <WFStatusCell
                                title={
                                  submittalWFStatus?.design_review_complete
                                    ?.label
                                }
                                value={
                                  submittalWFStatus?.design_review_complete
                                    ?.date
                                }
                                milestoneCompleted={
                                  submittalWFStatus?.design_review_complete
                                    ?.milestoneCompleted
                                }
                                data-testid="fls-review-date-cell"
                              />
                            </div>
                          )}
                      </div>
                      <div className="pr-2" data-testid="fls-view-link">
                        <Link
                          className="border-solid border-gray-300 hover:text-black hover:border-black border-[1px] rounded px-2 py-1 text-xs text-black"
                          to={{
                            pathname: `/project/${projectId}/submittals/${link?.id}`
                          }}
                          data-testid="fls-link-view-submittal"
                        >
                          View Submittal
                        </Link>
                      </div>
                    </div>
                  </div>
                </Panel>
              );
            })}
          </Collapse>
        ) : (
          <span className="text-xs text-neutral-400" data-testid="fls-no-links">
            No links added{" "}
            {canEditLinkingPage && (
              <Link
                data-testid="fls-link-to-add"
                to={`/project/${projectId}/schedule/material-linking?&materialId=${featureInstanceData?.id}`}
              >
                Click to add
              </Link>
            )}
            {hasPlannedSubmittalLink && (
              <div className="w-full px-3 py-2 border border-dashed text-black border-ciq-gray-300 rounded italic mt-1">
                Submittal Plan
              </div>
            )}
          </span>
        )}
      </Form.Item>
    </Form>
  );
}
